import React from 'react';
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  Package,
  CheckCircle,
  Clock,
  BarChart3,
  Building2,
  List,
  FolderOpen,
  Plus,
  TrendingUp,
  Activity,
  GitBranch,
  MessageCircle,
  History,
  BookOpen,
  Package as PackageIcon,
  ScrollText,
  Sparkles,
  Server,
  CalendarDays,
  SlidersHorizontal,
  X
} from 'lucide-react';
import { User } from '../../types';
import { ViewType } from '../../types/deploymentTypes';
import Avatar from '../common/Avatar';

// App-level views that aren't (yet) part of the shared ViewType union.
export type AppViewType = ViewType | 'my-preferences';

interface SidebarProps {
  currentUser: User;
  activeView: AppViewType;
  onViewChange: (view: AppViewType) => void;
  unreadMessagesCount?: number;
  // Below md the sidebar is an off-canvas drawer
  mobileOpen?: boolean;
  onClose?: () => void;
  // Admin-configured branding (GET /api/settings)
  appName?: string;
  companyName?: string;
  supportEmail?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  currentUser,
  activeView,
  onViewChange,
  unreadMessagesCount = 0,
  mobileOpen = false,
  onClose,
  appName,
  companyName,
  supportEmail
}) => {
  const getDeploymentMenuItems = () => {
    const baseItems = [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'my-dashboard', label: 'My Dashboard', icon: Sparkles },
    ];

    switch (currentUser.role) {
      case 'developer':
        return [
          ...baseItems,
          { id: 'submit', label: 'Create Request', icon: FileText },
          { id: 'my-requests', label: 'My Requests', icon: Clock },
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
        ];
      case 'tl':
        return [
          ...baseItems,
          { id: 'submit', label: 'Create Request', icon: FileText },
          { id: 'my-requests', label: 'My Requests', icon: Clock },
          { id: 'tl-approvals', label: 'TL Approvals', icon: CheckCircle },
          { id: 'slot-booking', label: 'Slot Booking', icon: Clock },
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'tl-meetings', label: 'Meetings', icon: CalendarDays },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'reports', label: 'Reports', icon: BarChart3 },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
        ];
      case 'pm':
        return [
          ...baseItems,
          { id: 'submit', label: 'Create Request', icon: FileText },
          { id: 'my-requests', label: 'My Requests', icon: Clock },
          { id: 'pm-approvals', label: 'PM Approvals', icon: CheckCircle },
          { id: 'slot-booking', label: 'Slot Booking', icon: Clock },
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'aws-server-inventory', label: 'AWS Servers', icon: Server },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'reports', label: 'Reports', icon: BarChart3 },
          { id: 'institute-status', label: 'Institute Status', icon: Building2 },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
        ];
      case 'devops':
        return [
          ...baseItems,
          { id: 'deployments', label: 'Deployments', icon: Package },
          { id: 'slot-booking', label: 'Slot Booking', icon: Clock },
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'aws-server-inventory', label: 'AWS Servers', icon: Server },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'institute-status', label: 'Institute Status', icon: Building2 },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
        ];
      case 'tester':
        return [
          ...baseItems,
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
        ];
      case 'admin':
        return [
          ...baseItems,
          { id: 'all-requests', label: 'All Requests', icon: FileText },
          { id: 'module-deployment-inventory', label: 'Module Inventory', icon: PackageIcon },
          { id: 'aws-server-inventory', label: 'AWS Servers', icon: Server },
          { id: 'tl-meetings', label: 'Meetings', icon: CalendarDays },
          { id: 'deployment-instructions', label: 'Instructions', icon: BookOpen },
          { id: 'reports', label: 'Reports', icon: BarChart3 },
          { id: 'institute-status', label: 'Institute Status', icon: Building2 },
          { id: 'changelog', label: 'Changelog', icon: ScrollText },
          { id: 'audit-log', label: 'Audit Log', icon: History },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'modules', label: 'Modules', icon: Package },
          { id: 'institutes', label: 'Institutes', icon: Building2 },
          { id: 'settings', label: 'Settings', icon: Settings },
        ];
      default:
        return baseItems;
    }
  };

  const getTaskMenuItems = () => {
    switch (currentUser.role) {
      case 'developer':
      case 'tl':
      case 'pm':
      case 'tester':
        return [
          { id: 'task-dashboard', label: 'Task Dashboard', icon: LayoutDashboard },
          { id: 'task-mytasks', label: 'My Tasks', icon: FolderOpen },
          { id: 'advanced-reports', label: 'Task Reports', icon: TrendingUp },
        ];
      case 'devops':
        return [
          { id: 'task-dashboard', label: 'Task Dashboard', icon: LayoutDashboard },
          { id: 'task-list', label: 'All Tasks', icon: List },
          { id: 'advanced-reports', label: 'Task Reports', icon: TrendingUp },
          { id: 'performance-dashboard', label: 'Performance', icon: Activity },
          { id: 'workflows', label: 'Workflows', icon: GitBranch },
        ];
      case 'admin':
        return [
          { id: 'task-dashboard', label: 'Task Dashboard', icon: LayoutDashboard },
          { id: 'task-list', label: 'All Tasks', icon: List },
          { id: 'advanced-reports', label: 'Task Reports', icon: TrendingUp },
          { id: 'performance-dashboard', label: 'Performance', icon: Activity },
          { id: 'workflows', label: 'Workflows', icon: GitBranch },
        ];
      default:
        return [];
    }
  };

  const deploymentMenuItems = getDeploymentMenuItems();
  const taskMenuItems = getTaskMenuItems();

  return (
    <>
    {/* Backdrop for the mobile drawer */}
    {mobileOpen && (
      <div
        className="fixed inset-0 bg-black/40 z-30 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
    )}
    <div
      className={`w-64 bg-white border-r border-gray-200 h-screen flex flex-col fixed left-0 top-0 z-40 transform transition-transform duration-200 md:translate-x-0 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900 truncate" title={appName || undefined}>{appName || 'Deployment'}</h1>
            <p className="text-xs text-gray-500">Deployment Management</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="md:hidden p-1 text-gray-500 hover:text-gray-800 rounded"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="mb-4">
          <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
            <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="sm" />
            <div>
              <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
              <p className="text-xs text-gray-500 capitalize">{currentUser.role}</p>
            </div>
          </div>
        </div>

        <nav className="space-y-1">
          {/* Deployment Management Section */}
          <div className="px-3 py-2 mb-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600">
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Deployment Management
            </p>
          </div>

          {/* Deployment Menu */}
          {deploymentMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                data-tour={`sidebar-${item.id}`}
                onClick={() => onViewChange(item.id as ViewType)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.label}
              </button>
            );
          })}

          {/* Separator for Task Management */}
          {taskMenuItems.length > 0 && (
            <>
              <div className="py-3">
                <div className="border-t border-gray-200"></div>
              </div>
              <div className="px-3 py-2 mb-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-600">
                <p className="text-xs font-bold text-purple-700 uppercase tracking-wider flex items-center">
                  <List className="w-4 h-4 mr-2" />
                  Task Management
                </p>
              </div>
              
              {/* Create Task Button */}
              <button
                onClick={() => onViewChange('task-create' as ViewType)}
                className={`w-full flex items-center px-3 py-2.5 text-sm font-semibold rounded-lg transition-colors mb-2 ${
                  activeView === 'task-create'
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <Plus className="mr-2 h-5 w-5" />
                Create Task
              </button>
            </>
          )}

          {/* Task Menu */}
          {taskMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                data-tour={`sidebar-${item.id}`}
                onClick={() => onViewChange(item.id as ViewType)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.label}
              </button>
            );
          })}

          {/* Separator for Timesheets */}
          {(currentUser.role === 'developer' || currentUser.role === 'tl' || currentUser.role === 'pm' || currentUser.role === 'tester' || currentUser.role === 'devops' || currentUser.role === 'admin') && (
            <>
              <div className="py-3">
                <div className="border-t border-gray-200"></div>
              </div>
              <div className="px-3 py-2 mb-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-l-4 border-orange-600">
                <p className="text-xs font-bold text-orange-700 uppercase tracking-wider flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Timesheets
                </p>
              </div>

              {/* Timesheet Menu Items — admins review others' timesheets but don't file their own */}
              {currentUser.role !== 'admin' && (
              <button
                onClick={() => onViewChange('my-timesheets' as ViewType)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeView === 'my-timesheets'
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <FolderOpen className="mr-3 h-5 w-5" />
                My Timesheets
              </button>
              )}

              {/* Team Timesheets - Show for TLs, PMs, Admin, and DevOps (any devops user
                  can be another devops user's reportingTo manager, same rule as
                  TimesheetEntryForm's devops-eligible-approver list) */}
              {(currentUser.role === 'tl' ||
                currentUser.role === 'pm' ||
                currentUser.role === 'admin' ||
                currentUser.role === 'devops') && (
                <button
                  onClick={() => onViewChange('team-timesheets' as ViewType)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeView === 'team-timesheets'
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Users className="mr-3 h-5 w-5" />
                  Team Timesheets
                </button>
              )}
            </>
          )}

          {/* Separator for Chat */}
          <div className="py-3">
            <div className="border-t border-gray-200"></div>
          </div>
          <div className="px-3 py-2 mb-2 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border-l-4 border-green-600">
            <p className="text-xs font-bold text-green-700 uppercase tracking-wider flex items-center">
              <MessageCircle className="w-4 h-4 mr-2" />
              Communication
            </p>
          </div>

          {/* Chat Menu Item with Notification Badge */}
          <button
            data-tour="sidebar-chat"
            onClick={() => onViewChange('chat' as ViewType)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors relative ${
              activeView === 'chat'
                ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                : unreadMessagesCount > 0
                ? 'text-gray-900 hover:text-gray-900 hover:bg-gray-50 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center relative">
              <MessageCircle className={`mr-3 h-5 w-5 ${unreadMessagesCount > 0 ? 'text-red-600' : ''}`} />
              {unreadMessagesCount > 0 && activeView !== 'chat' && (
                <span className="absolute -top-1 left-0 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
              )}
              <span>Chat</span>
            </div>
            {unreadMessagesCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 font-bold animate-pulse shadow-lg">
                {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
              </span>
            )}
          </button>

          {/* Personal notification preferences — every role */}
          <button
            data-tour="sidebar-my-preferences"
            onClick={() => onViewChange('my-preferences')}
            className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeView === 'my-preferences'
                ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal className="mr-3 h-5 w-5" />
            My Preferences
          </button>
        </nav>
      </div>

      {(companyName || supportEmail) && (
        <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0 text-xs text-gray-500 space-y-0.5">
          {companyName && <p className="truncate" title={companyName}>{companyName}</p>}
          {supportEmail && (
            <a href={`mailto:${supportEmail}`} className="block truncate text-blue-600 hover:underline" title={supportEmail}>
              {supportEmail}
            </a>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default Sidebar;